import {useMemo} from 'react';

import {usePanelSearchDebouncedContext} from '../../../state/panelbank/PanelSearchContextProvider';
import {panelIsActive, searchRegexMatchesPanel} from '../../../util/panelbank';
import {useKeyTypes} from '../../../util/runhelpers';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {useSingleRunModeContext} from '../../MultiRunWorkspace/SingleRunMode';
import {getIsSectionNameSearchMatch} from '../sectionLayout/useSectionNameMatch';
import {useWorkspaceSections} from '../sections/useWorkspaceSections';

/**
 * Returns the count of panels and sections matching the current search.
 * Optimized with two-phase computation:
 * 1. EXPENSIVE: Compute active panels when run data changes (historyKeyInfo, etc.)
 * 2. CHEAP: Count search matches when searchRegex changes
 */
export const useSearchResultCounts = () => {
  const visibleSections = useWorkspaceSections();
  const {debouncedSearchRegex: searchRegex} = usePanelSearchDebouncedContext();

  // Get context for filtering active panels (same as useSectionPanels)
  const {isSingleRun} = useSingleRunModeContext();
  const {
    keyInfoQuery: {historyKeyInfo},
  } = useKeyInfoQueryContext();
  const keyTypes = useKeyTypes(historyKeyInfo.keys);

  // PHASE 1: Compute active panels (expensive, but should update when runs data changes)
  // This can change when a run is deleted, added, hidden, etc
  const sectionsWithActivePanels = useMemo(() => {
    return visibleSections.map(section => ({
      sectionName: section.name,
      activePanels: section.panels.filter(panel =>
        panelIsActive({
          section,
          panel,
          isSingleRun,
          historyKeyInfo,
          keyTypes,
        })
      ),
    }));
  }, [visibleSections, isSingleRun, historyKeyInfo, keyTypes]);

  // PHASE 2: Count matches (cheap, runs when searchRegex changes)
  return useMemo(() => {
    let numPanelsMatched = 0;
    let numSectionsMatched = 0;

    if (!searchRegex) {
      return {numPanelsMatched, numSectionsMatched};
    }

    for (const {sectionName, activePanels} of sectionsWithActivePanels) {
      const isSectionNameSearchMatch = getIsSectionNameSearchMatch(
        sectionName,
        searchRegex
      );

      // If the section name matches the search, return all active panels
      // This is to avoid showing empty sections when searching by section name
      if (isSectionNameSearchMatch) {
        numSectionsMatched++;
        numPanelsMatched += activePanels.length;
        continue;
      }

      for (const panel of activePanels) {
        if (searchRegexMatchesPanel(searchRegex, panel)) {
          numPanelsMatched++;
        }
      }
    }

    return {
      numPanelsMatched,
      numSectionsMatched,
    };
  }, [sectionsWithActivePanels, searchRegex]);
};
